/**
 * Framework Mapper Component - Canonical Enterprise Structure
 *
 * Node-centric architecture compliance:
 * - Uses proper relational mapping API endpoints
 * - Never mutates template JSON
 * - Proper node-based selection flow: Template → Node → Framework
 * - Mappings have their own primary keys with full audit trail
 *
 * Canonical table: framework_mappings
 * - id, framework_id, framework_version_id, template_node_id
 * - external_clause_ref, mapping_type, notes, created_at
 */

import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  GitBranch,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  AlertTriangle,
  FileText,
  Link,
  CheckCircle,
  Wand2,
} from 'lucide-react';
import { notify } from '@/core/notifications/notify';
import { useModal, ConfirmModal, FrameworkMappingModal, FrameworkAssignmentWizard } from '@/components/modal';
import { useAuth } from '@/contexts/AuthContext';
import PageInfoButton from '@/components/PageInfoButton';
import { frameworkMapperApi } from '@domains/compliance/definition/api/frameworkMapper.api';
import { frameworkVersionsApi } from '@domains/compliance/definition/frameworkVersions';
import {
  FrameworkMappingResponse,
  TemplateNode,
  CreateFrameworkMappingRequest,
  UpdateFrameworkMappingRequest
} from '@domains/compliance/definition/api/frameworkMapper.dto';
import { FrameworkVersion } from '@domains/compliance/definition/frameworkVersions';

/**
 * UI State for mapping form - matches canonical structure
 */
interface MappingFormData {
  template_id: number;
  template_node_id: number;
  framework_id: number;
  framework_version_id: number;
  external_clause_ref: string;
  mapping_type: 'direct' | 'partial' | 'indirect';
  notes: string;
}

export const FrameworkMapper: React.FC = () => {
  const { hasPermission } = useAuth();
  const { openModal, closeModal } = useModal();

  const canViewMappings = hasPermission('frameworks.read');
  const canEditMappings = hasPermission('frameworks.update');

  // Data state
  const [templates, setTemplates] = useState<any[]>([]);
  const [templateNodes, setTemplateNodes] = useState<TemplateNode[]>([]);
  const [frameworkVersions, setFrameworkVersions] = useState<FrameworkVersion[]>([]);
  const [mappings, setMappings] = useState<FrameworkMappingResponse[]>([]);
  const [_currentModalId, setCurrentModalId] = useState<string | null>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingNodes, setLoadingNodes] = useState(false);
  const [loadingMappings, setLoadingMappings] = useState(false);

  // Form state
  const [formData, setFormData] = useState<MappingFormData>({
    template_id: 0,
    template_node_id: 0,
    framework_id: 0,
    framework_version_id: 0,
    external_clause_ref: '',
    mapping_type: 'direct',
    notes: ''
  });
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<number>>(new Set());

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================
  useEffect(() => {
    loadTemplates();
    loadFrameworkVersions();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await frameworkMapperApi.getTemplates();
      setTemplates(data);
    } catch (error) {
      notify.error('Failed to load templates');
    }
  };

  const loadFrameworkVersions = async () => {
    try {
      const data = await frameworkVersionsApi.getActiveFrameworkVersions();
      setFrameworkVersions(data);
    } catch (error) {
      notify.error('Failed to load framework versions');
    }
  };

  const loadTemplateNodes = async (templateId: number) => {
    if (!templateId) { setTemplateNodes([]); return; }
    setLoadingNodes(true);
    try {
      const data = await frameworkMapperApi.getTemplateNodes(templateId);
      setTemplateNodes(data);
    } catch (error) {
      notify.error('Failed to load template nodes');
      setTemplateNodes([]);
    } finally {
      setLoadingNodes(false);
    }
  };

  const loadMappings = async (templateNodeId: number) => {
    if (!templateNodeId) { setMappings([]); return; }
    setLoadingMappings(true);
    try {
      const data = await frameworkMapperApi.getNodeMappings(templateNodeId);
      setMappings(data);
    } catch (error) {
      notify.error('Failed to load mappings');
      setMappings([]);
    } finally {
      setLoadingMappings(false);
    }
  };

  // ==========================================================================
  // EVENT HANDLERS
  // ==========================================================================

  const handleTemplateChange = async (templateId: number) => {
    setFormData({ ...formData, template_id: templateId, template_node_id: 0, framework_id: 0, framework_version_id: 0, external_clause_ref: '', mapping_type: 'direct', notes: '' });
    setTemplateNodes([]);
    setMappings([]);
    setSelectedNodeIds(new Set());
    if (templateId) await loadTemplateNodes(templateId);
  };

  const handleNodeChange = async (nodeId: number) => {
    setFormData({ ...formData, template_node_id: nodeId, framework_id: 0, framework_version_id: 0, external_clause_ref: '', mapping_type: 'direct', notes: '' });
    setMappings([]);
    if (nodeId) await loadMappings(nodeId);
  };

  const handleFrameworkVersionChange = (frameworkVersionId: number) => {
    const fv = frameworkVersions.find(fv => fv.id === frameworkVersionId);
    if (fv) {
      setFormData({ ...formData, framework_id: fv.framework_id, framework_version_id: fv.id, external_clause_ref: fv.version });
    }
  };

  const handleCreate = async (mappingData: any) => {
    if (!mappingData.template_id || !mappingData.template_node_id || !mappingData.framework_id) {
      notify.error('Please select a template, node, and framework');
      return;
    }
    setLoading(true);
    try {
      await frameworkMapperApi.createMapping({
        framework_id: mappingData.framework_id,
        framework_version_id: mappingData.framework_version_id,
        template_node_id: mappingData.template_node_id,
        external_clause_ref: mappingData.external_clause_ref || undefined,
        mapping_type: mappingData.mapping_type,
        notes: mappingData.notes || undefined
      });
      notify.success('Mapping created successfully');
      if (formData.template_node_id) await loadMappings(formData.template_node_id);
    } catch (error: any) {
      notify.error(error.message || 'Failed to create mapping');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (mappingData: any, selectedMapping: FrameworkMappingResponse) => {
    setLoading(true);
    try {
      await frameworkMapperApi.updateMapping(selectedMapping.id, {
        external_clause_ref: mappingData.external_clause_ref || undefined,
        mapping_type: mappingData.mapping_type,
        notes: mappingData.notes || undefined
      });
      notify.success('Mapping updated successfully');
      if (formData.template_node_id) await loadMappings(formData.template_node_id);
    } catch (error: any) {
      notify.error(error.message || 'Failed to update mapping');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (mapping: FrameworkMappingResponse) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Delete Mapping',
        message: 'Are you sure you want to delete this mapping? This action cannot be undone.',
        confirmText: 'Delete', cancelText: 'Cancel', type: 'danger',
        onConfirm: async () => {
          setLoading(true);
          try {
            await frameworkMapperApi.deleteMapping(mapping.id);
            notify.success('Mapping deleted');
            if (formData.template_node_id) await loadMappings(formData.template_node_id);
          } catch (error: any) {
            notify.error(error.message || 'Failed to delete mapping');
          } finally {
            setLoading(false);
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleBulkOrSingleMap = () => {
    if (selectedNodeIds.size > 1 && formData.framework_id > 0) {
      const modalId = openModal({
        component: ConfirmModal,
        props: {
          title: `Map ${selectedNodeIds.size} Selected Nodes`,
          message: `This will create a direct mapping for ${selectedNodeIds.size} selected nodes to the chosen framework. Already-mapped nodes will be skipped.`,
          confirmText: `Map ${selectedNodeIds.size} Nodes`,
          type: 'info' as const,
          onConfirm: async () => {
            closeModal(modalId);
            setLoading(true);
            let created = 0, skipped = 0;
            const skippedNames: string[] = [];
            for (const nodeId of selectedNodeIds) {
              const node = templateNodes.find(n => n.id === nodeId);
              if (!node) continue;
              try {
                await frameworkMapperApi.createMapping({
                  framework_id: formData.framework_id,
                  framework_version_id: formData.framework_version_id,
                  template_node_id: node.id,
                  external_clause_ref: (node as any).code || undefined,
                  mapping_type: formData.mapping_type || 'direct',
                });
                created++;
              } catch {
                skipped++;
                skippedNames.push(node.name);
              }
            }
            setLoading(false);
            const msg = `Mapped ${created} node${created !== 1 ? 's' : ''}.` +
              (skipped > 0 ? ` ${skipped} skipped (already mapped).` : '');
            notify.success(msg);
            setSelectedNodeIds(new Set());
            if (formData.template_node_id) await loadMappings(formData.template_node_id);
          },
          onCancel: () => closeModal(modalId),
        }
      });
    } else {
      const nodeId = selectedNodeIds.size === 1 ? Array.from(selectedNodeIds)[0] : formData.template_node_id;
      if (nodeId && nodeId !== formData.template_node_id) handleNodeChange(nodeId);
      const modalId = openModal({
        component: FrameworkMappingModal,
        props: {
          onSubmit: handleCreate,
          selectedMapping: null,
          initialData: { ...formData, template_node_id: nodeId || formData.template_node_id },
          onSuccess: () => { closeModal(modalId); setCurrentModalId(null); },
        },
        options: { size: 'large' }
      });
      setCurrentModalId(modalId);
    }
  };

  const startEdit = (mapping: FrameworkMappingResponse) => {
    const modalId = openModal({
      component: FrameworkMappingModal,
      props: {
        onSubmit: handleUpdate,
        selectedMapping: mapping,
        onSuccess: () => { closeModal(modalId); setCurrentModalId(null); },
      },
      options: { size: 'large' }
    });
    setCurrentModalId(modalId);
  };

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  const getTemplateName = (templateId: number) => {
    const t = templates.find(t => t.id === templateId);
    return t ? `${t.name} ${t.version ? `(v${t.version})` : ''}` : `Template ${templateId}`;
  };

  const getNodeName = (nodeId: number) => templateNodes.find(n => n.id === nodeId)?.name || `Node ${nodeId}`;

  const getFrameworkVersionName = (fvId: number) => {
    const fv = frameworkVersions.find(fv => fv.id === fvId);
    return fv ? `${fv.framework_name || 'Framework'} - ${fv.version}` : `Version ${fvId}`;
  };

  const getNodeTypeLabel = (nodeType: string) => {
    const labels: Record<string, string> = { scope: 'Scope', section: 'Section', page: 'Page', control: 'Control', metric: 'Metric', requirement: 'Requirement' };
    return labels[nodeType] || nodeType;
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  if (!canViewMappings) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-8 max-w-md">
          <AlertTriangle className="text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">You don't have permission to access Framework Mapping.</p>
        </div>
      </div>
    );
  }

  if (loading && templates.length === 0 && frameworkVersions.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-slate-900 text-left">Framework Mapping</h1>
              <PageInfoButton />
            </div>
            <p className="text-slate-500 text-sm mt-0.5 text-left">Connect compliance frameworks to your template structures</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => openModal({ component: FrameworkAssignmentWizard, props: { onComplete: () => {} }, options: { size: 'large' } })} className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-medium text-sm rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
              <Wand2 size={16} /> Quick Assign
            </button>
            <button
              onClick={handleBulkOrSingleMap}
              disabled={(!formData.template_node_id && selectedNodeIds.size === 0) || !formData.framework_id || !canEditMappings}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {selectedNodeIds.size > 1 ? (
                <><CheckCircle size={16} /> Map {selectedNodeIds.size} Nodes</>
              ) : selectedNodeIds.size === 1 ? (
                <><Plus size={16} /> Map Selected Node</>
              ) : (
                <><Plus size={16} /> Select Nodes to Map</>
              )}
            </button>
          </div>
        </div>

        {/* Bulk-ops notice — mapping is now inline in the builder */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 mb-6">
          <p className="text-xs text-slate-600">
            <strong>Advanced view</strong> — mapping now happens inline in the Builder via clause references.
            Use this page only for bulk node-to-framework mapping operations.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Templates</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{templates.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-emerald-200 p-4 bg-emerald-50/50">
            <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider text-left">Framework Versions</p>
            <p className="text-2xl font-bold text-emerald-800 mt-1">{frameworkVersions.length}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Active Mappings</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{mappings.length}</p>
          </div>
        </div>

        {/* Selection Steps — 3-column */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">

          {/* Step 1: Template */}
          <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50/50">
              <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">1</span>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Select Template</h3>
              </div>
            </div>
            <div className="p-4">
              <select
                value={formData.template_id}
                onChange={(e) => handleTemplateChange(parseInt(e.target.value))}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
                disabled={loading}
              >
                <option value={0}>Select a template...</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} {t.version ? `(v${t.version})` : ''} {t.generation_source === 'AI_PARSER' ? '(AI)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-2">Only published templates are listed. Publish your template in the <RouterLink to="/compliance/definition/templates" className="text-blue-600 hover:text-blue-800 underline">Templates page</RouterLink> before mapping it to a framework.</p>
            </div>
          </div>

          {/* Step 2: Nodes */}
          <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 bg-emerald-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xs">2</span>
                </div>
                <h3 className="text-sm font-semibold text-slate-800">Select Nodes</h3>
              </div>
              {selectedNodeIds.size > 0 && (
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">{selectedNodeIds.size} selected</span>
              )}
            </div>
            <div className="p-3">
              {loadingNodes ? (
                <div className="flex items-center justify-center py-6">
                  <RefreshCw className="text-blue-600 animate-spin" size={20} />
                  <p className="text-slate-500 text-sm ml-2">Loading nodes...</p>
                </div>
              ) : templateNodes.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">Select a template first</p>
              ) : (
                <>
                  {/* Select all toggle */}
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedNodeIds.size === templateNodes.length && templateNodes.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedNodeIds(new Set(templateNodes.map(n => n.id)));
                          else setSelectedNodeIds(new Set());
                        }}
                        className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-xs font-medium text-slate-600">
                        {selectedNodeIds.size === templateNodes.length ? 'Deselect All' : 'Select All'} ({templateNodes.length})
                      </span>
                    </label>
                  </div>

                  {/* Node list */}
                  <div className="max-h-48 overflow-y-auto space-y-0.5">
                    {templateNodes.map(node => {
                      const depth = node.node_type === 'section' ? 1 : node.node_type === 'requirement' ? 2 : (node.node_type as string) === 'sub_requirement' ? 3 : 0;
                      return (
                        <label key={node.id} className="flex items-start gap-2 py-1 px-2 rounded hover:bg-slate-50 cursor-pointer transition-colors" style={{ paddingLeft: 8 + depth * 12 }}>
                          <input
                            type="checkbox"
                            checked={selectedNodeIds.has(node.id)}
                            onChange={(e) => {
                              const next = new Set(selectedNodeIds);
                              if (e.target.checked) next.add(node.id); else next.delete(node.id);
                              setSelectedNodeIds(next);
                              if (e.target.checked && next.size === 1) handleNodeChange(node.id);
                            }}
                            className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 shrink-0 mt-0.5"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className={`shrink-0 text-[8px] font-bold uppercase px-1 py-0.5 rounded ${
                                node.node_type === 'scope' ? 'bg-blue-100 text-blue-700' :
                                node.node_type === 'section' ? 'bg-emerald-100 text-emerald-700' :
                                node.node_type === 'requirement' ? 'bg-purple-100 text-purple-700' :
                                'bg-slate-100 text-slate-500'
                              }`}>{(node.node_type as string) === 'sub_requirement' ? 'SUB' : node.node_type.slice(0, 3).toUpperCase()}</span>
                              <span className={`text-[11px] leading-tight ${node.node_type === 'scope' ? 'font-bold text-slate-800' : node.node_type === 'section' ? 'font-semibold text-slate-700' : 'text-slate-600'}`}>
                                {node.name}
                              </span>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Step 3: Framework */}
          <div className={`bg-white rounded-3xl border border-slate-100 overflow-hidden transition-opacity ${selectedNodeIds.size > 0 ? '' : 'opacity-40 pointer-events-none'}`}>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50/50">
              <div className="w-7 h-7 bg-slate-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">3</span>
              </div>
              <h3 className="text-sm font-semibold text-slate-800">Select Framework</h3>
            </div>
            <div className="p-4">
              {frameworkVersions.length === 0 ? (
                <div className="text-center py-4 bg-amber-50 border border-amber-200 rounded-lg px-3">
                  <AlertTriangle className="text-amber-500 mx-auto mb-2" size={20} />
                  <p className="text-xs text-amber-800">
                    No active framework versions.{' '}
                    <RouterLink to="/compliance/definition/frameworks" className="text-blue-600 underline font-medium">Create one</RouterLink>
                  </p>
                </div>
              ) : (
                <select
                  value={formData.framework_version_id}
                  onChange={(e) => handleFrameworkVersionChange(parseInt(e.target.value))}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
                  disabled={loading}
                >
                  <option value={0}>Select a framework version...</option>
                  {frameworkVersions
                    .sort((a, b) => {
                      const fa = a.framework_name || '';
                      const fb = b.framework_name || '';
                      if (fa !== fb) return fa.localeCompare(fb);
                      return a.version.localeCompare(b.version);
                    })
                    .map(fv => (
                      <option key={fv.id} value={fv.id}>
                        {fv.framework_name || 'Framework'} - {fv.version}
                      </option>
                    ))}
                </select>
              )}
            </div>
          </div>
        </div>

        {/* Mappings Table */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">
                Mappings{formData.template_node_id ? `: ${getNodeName(formData.template_node_id)}` : ''}
              </h2>
              <p className="text-xs text-slate-500">Framework to template node mappings</p>
            </div>
            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">{mappings.length} mapping{mappings.length !== 1 ? 's' : ''}</span>
          </div>

          {loadingMappings ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="text-blue-600 animate-spin" size={24} />
              <p className="text-slate-500 text-sm ml-3">Loading mappings...</p>
            </div>
          ) : mappings.length === 0 ? (
            <div className="py-12 px-6">
              <Link className="text-slate-300 mb-4" size={40} />
              <h3 className="text-base font-semibold text-slate-700 mb-1">No Mappings</h3>
              <p className="text-sm text-slate-500 max-w-sm mb-4">
                {formData.template_node_id > 0
                  ? 'No mappings for this node yet. Select a framework and click the map button above.'
                  : 'To see mappings, select a template (step 1), select nodes (step 2), choose a framework (step 3), then click "Map Nodes".'}
              </p>
              {formData.template_node_id > 0 && canEditMappings && (
                <button
                  onClick={() => {
                    openModal({
                      component: FrameworkMappingModal,
                      props: { onSubmit: handleCreate, selectedMapping: null, initialData: formData },
                      options: { size: 'large' }
                    });
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                >
                  <Plus size={16} /> Create First Mapping
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead>
                  <tr className="bg-slate-50/50">
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Template</th>
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Node</th>
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Framework Version</th>
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Clause Ref</th>
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Type</th>
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Notes</th>
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Created</th>
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {mappings.map(mapping => (
                    <tr key={mapping.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 text-left text-sm font-medium text-slate-900 whitespace-nowrap">
                        {mapping.template_node ? getTemplateName(mapping.template_node.template_id) : '-'}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-left">
                        <p className="text-sm font-medium text-slate-900">{mapping.template_node?.name || '-'}</p>
                        <p className="text-xs text-slate-500">{mapping.template_node ? getNodeTypeLabel(mapping.template_node.node_type) : '-'}</p>
                      </td>
                      <td className="px-5 py-3 text-left text-sm text-slate-700 whitespace-nowrap">
                        {getFrameworkVersionName(mapping.framework_version_id)}
                      </td>
                      <td className="px-5 py-3 text-left text-sm text-slate-600 max-w-[120px] truncate">
                        {mapping.external_clause_ref || '-'}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-left">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                          mapping.mapping_type === 'direct' ? 'bg-emerald-100 text-emerald-800' :
                          mapping.mapping_type === 'partial' ? 'bg-amber-100 text-amber-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {mapping.mapping_type || 'direct'}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-left text-sm text-slate-500 max-w-[150px] truncate">
                        {mapping.notes || '-'}
                      </td>
                      <td className="px-5 py-3 text-left text-xs text-slate-500 whitespace-nowrap">
                        {mapping.created_at ? new Date(mapping.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-left">
                        {canEditMappings && (
                          <div className="flex items-center gap-1">
                            <button onClick={() => startEdit(mapping)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                              <Edit size={14} />
                            </button>
                            <button onClick={() => handleDelete(mapping)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FrameworkMapper;
